import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Receipt,
  LocalShipping,
  Print,
  Add as AddIcon,
  Search,
  FilterList,
  MoreVert,
  ViewColumn,
  Close
} from '@mui/icons-material';
import { orderService } from '../services/orderService';
import { quoteService } from '../services/quoteService';
import { customerService } from '../services/customerService';
import { packingSlipService } from '../services/packingSlipService';
import { format } from 'date-fns';
import { formatDateOnly, formatLocalTodayYmd, parseLocalDate } from '../utils/dateUtils';
import { formatCurrency } from '../components/CurrencyTextField';
import {
  ProcurementSummaryNotesDense,
  procurementSummaryFieldsRowSx
} from '../components/procurement/ProcurementSummaryBar';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import { printWithFilename } from '../utils/printUtils';

const ORDER_LIST_HEADER_FILTERS_INITIAL = {
  order: '',
  quote: '',
  dateTime: '',
  dueDate: '',
  customer: '',
  po: '',
  total: '',
  uninvoiced: '',
  openAr: '',
  salesman: '',
  status: '',
  notes: ''
};

const ORDER_LIST_FILTERS_DEFAULTS = {
  tableSearch: '',
  headerColFilters: { ...ORDER_LIST_HEADER_FILTERS_INITIAL },
  colDateFrom: '',
  colDateTo: '',
  sortKey: 'order_date',
  sortDirection: 'desc',
  page: 0
};

function trimHeaderFilterObj(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));
}

/** Maps debounced header keys to GET /api/work-orders query params. */
function workOrderColumnFilterQueryParams(d) {
  const out = {};
  if (d.order) out.filter_order = d.order;
  if (d.quote) out.filter_quote = d.quote;
  if (d.dateTime) {
    out.filter_order_date = d.dateTime;
    out.filter_time = d.dateTime;
  }
  if (d.dueDate) out.filter_due_date = d.dueDate;
  if (d.customer) out.filter_customer = d.customer;
  if (d.po) out.filter_po = d.po;
  if (d.total) out.filter_total = d.total;
  if (d.uninvoiced) out.filter_uninvoiced = d.uninvoiced;
  if (d.openAr) out.filter_open_ar = d.openAr;
  if (d.salesman) out.filter_salesman = d.salesman;
  if (d.status) out.filter_status = d.status;
  if (d.notes) out.filter_notes = d.notes;
  return out;
}

/** Must match `minWidths` passed to `useResizableColumns` — keeps columns from shrinking until headers letter-wrap. */
const ORDER_COL_MIN_WIDTHS = {
  order: 120,
  quote: 100,
  dateTime: 110,
  dueDate: 90,
  customer: 160,
  customerPo: 110,
  total: 100,
  uninvoiced: 90,
  openAr: 90,
  salesman: 120,
  status: 100,
  notes: 160,
  actions: 100
};

const ORDER_COLUMNS = [
  { key: 'order', label: 'Order' },
  { key: 'quote', label: 'Quote #' },
  { key: 'dateTime', label: 'Date' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'customer', label: 'Customer' },
  { key: 'customerPo', label: 'Customer PO' },
  { key: 'total', label: 'Order Total' },
  { key: 'uninvoiced', label: 'Uninvoiced' },
  { key: 'openAr', label: 'Open AR' },
  { key: 'salesman', label: 'Salesman' },
  { key: 'status', label: 'Status' },
  { key: 'notes', label: 'Notes' },
  { key: 'actions', label: 'Actions' }
];

const ORDER_COL_VISIBILITY_STORAGE_KEY = 'wm_orders_col_visible';

function defaultOrderColVisible() {
  return Object.fromEntries(ORDER_COLUMNS.map((c) => [c.key, true]));
}

function loadOrderColVisible() {
  try {
    const raw = window.localStorage.getItem(ORDER_COL_VISIBILITY_STORAGE_KEY);
    if (!raw) return defaultOrderColVisible();
    const parsed = JSON.parse(raw);
    const base = defaultOrderColVisible();
    for (const col of ORDER_COLUMNS) {
      if (typeof parsed[col.key] === 'boolean') base[col.key] = parsed[col.key];
    }
    if (typeof parsed.dateTime !== 'boolean') {
      const legacyDate = parsed.date !== false;
      const legacyTime = parsed.time !== false;
      if (parsed.date !== undefined || parsed.time !== undefined) {
        base.dateTime = legacyDate || legacyTime;
      }
    }
    return base;
  } catch {
    return defaultOrderColVisible();
  }
}

function saveOrderColVisible(visible) {
  try {
    window.localStorage.setItem(ORDER_COL_VISIBILITY_STORAGE_KEY, JSON.stringify(visible));
  } catch {
    /* ignore */
  }
}

function isOrderColVisible(key, visible) {
  return visible[key] !== false;
}

/** Stack title + filter; avoid `minWidth: 0` here or table columns can collapse past readable width. */
const orderColHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const orderColHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden',
  '& .MuiTableSortLabel-root': {
    maxWidth: '100%',
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    '& .MuiTableSortLabel-icon': {
      flexShrink: 0
    }
  }
};

const orderColHeaderCaptionInSlotSx = {
  fontSize: '0.65rem',
  fontWeight: 700,
  lineHeight: 1.25,
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%'
};

const orderHeaderFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

const WorkOrders = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get('customer');
  const partFilter = searchParams.get('part_id');
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_orders_list_filters', ORDER_LIST_FILTERS_DEFAULTS);
  const tableSearch = listFilters.tableSearch;
  const setTableSearch = useCallback((v) => setListFilter('tableSearch', v), [setListFilter]);
  const colDateFrom = listFilters.colDateFrom;
  const setColDateFrom = useCallback((v) => setListFilter('colDateFrom', v), [setListFilter]);
  const colDateTo = listFilters.colDateTo;
  const setColDateTo = useCallback((v) => setListFilter('colDateTo', v), [setListFilter]);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const sortKey = listFilters.sortKey;
  const setSortKey = useCallback((v) => setListFilter('sortKey', v), [setListFilter]);
  const sortDirection = listFilters.sortDirection;
  const setSortDirection = useCallback((v) => setListFilter('sortDirection', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    if (customerFilter || partFilter) {
      const next = new URLSearchParams(searchParams);
      next.delete('customer');
      next.delete('part_id');
      setSearchParams(next, { replace: true });
    }
  }, [clearListFilters, customerFilter, partFilter, searchParams, setSearchParams]);
  const [showCancelledOrders, setShowCancelledOrders] = useState(() => {
    try {
      return window.localStorage.getItem('wm_orders_show_cancelled') === 'true';
    } catch {
      return false;
    }
  });
  const [showSupersededOrders, setShowSupersededOrders] = useState(() => {
    try {
      return window.localStorage.getItem('wm_orders_show_superseded') === 'true';
    } catch {
      return false;
    }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** Debounced value used for API list + pagination (avoids a request per keystroke). */
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(() => String(tableSearch || '').trim());
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrderForHeader, setSelectedOrderForHeader] = useState(null);
  /** Sum of amount_paid on invoices for the selected order (from full order fetch). */
  const [headerAmtPaid, setHeaderAmtPaid] = useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() =>
    trimHeaderFilterObj(headerColFilters || ORDER_LIST_HEADER_FILTERS_INITIAL)
  );
  const [packingSlipBusyOrderId, setPackingSlipBusyOrderId] = useState(null);
  const [packingSlipByOrderId, setPackingSlipByOrderId] = useState({});
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_orders_rows_per_page'), 10);
      return [50, 100, 200].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });
  const [newTmDialogOpen, setNewTmDialogOpen] = useState(false);
  const [tmCustomer, setTmCustomer] = useState(null);
  const [tmDescription, setTmDescription] = useState('');
  const [tmPoNumber, setTmPoNumber] = useState('');
  const [tmSaving, setTmSaving] = useState(false);
  const [tmCustomers, setTmCustomers] = useState([]);
  const [newOrderStyle, setNewOrderStyle] = useState('standard');

  // Aligned IDs: YYO-##### or YYQ-#####; revisions are -2, -3, … (not YYO-##### alone — that has a false match with /.+-\d+$/)
  const hasOrderRevisionSuffix = (identifier) => {
    const m = String(identifier || '').trim().match(/^(\d{2})O-(\d{5,})(?:-(\d+))?(?:r\d+)?$/i);
    return !!(m && m[3] != null && parseInt(m[3], 10) >= 2);
  };
  const hasQuoteRevisionSuffix = (identifier) => {
    const m = String(identifier || '').trim().match(/^(\d{2})Q-(\d{5,})(?:-(\d+))?(?:r\d+)?$/i);
    return !!(m && m[3] != null && parseInt(m[3], 10) >= 2);
  };
  const DEFAULT_ORDER_COL_WIDTHS = {
    order: 180,
    quote: 120,
    dateTime: 120,
    dueDate: 110,
    customer: 240,
    customerPo: 140,
    total: 120,
    uninvoiced: 110,
    openAr: 110,
    salesman: 150,
    status: 120,
    notes: 280,
    actions: 120
  };
  const {
    colWidths,
    setColWidths,
    startResize,
    saveColWidths,
    resetColWidths
  } = useResizableColumns({
    defaultWidths: DEFAULT_ORDER_COL_WIDTHS,
    storageKey: 'wm_orders_col_widths',
    minWidths: ORDER_COL_MIN_WIDTHS
  });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [colVisible, setColVisible] = useState(loadOrderColVisible);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionDialogTitle, setRevisionDialogTitle] = useState('');
  const [revisionDialogItems, setRevisionDialogItems] = useState([]);
  const [revisionDialogLoading, setRevisionDialogLoading] = useState(false);
  const [revisionDialogType, setRevisionDialogType] = useState('order'); // 'order' | 'quote'

  const orderListColumnCellSx = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(ORDER_COL_MIN_WIDTHS).map((k) => [
          k,
          { width: colWidths[k], minWidth: ORDER_COL_MIN_WIDTHS[k] }
        ])
      ),
    [colWidths]
  );

  const visibleOrderColumnCount = useMemo(
    () => ORDER_COLUMNS.filter((c) => isOrderColVisible(c.key, colVisible)).length,
    [colVisible]
  );

  const resetOrderColumnSettings = () => {
    resetColWidths();
    const defVis = defaultOrderColVisible();
    setColVisible(defVis);
    saveOrderColVisible(defVis);
  };

  const handleSort = (key) => {
    setSortDirection((prevDir) => (sortKey === key ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  useEffect(() => {
    if (!newTmDialogOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await customerService.getAll({ limit: 500, activeOnly: true });
        const list = res?.data ?? [];
        if (!cancelled) setTmCustomers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setTmCustomers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [newTmDialogOpen]);

  const handleCreateTmOrder = async () => {
    if (!tmCustomer?.id) {
      setError('Select a customer.');
      return;
    }
    try {
      setTmSaving(true);
      setError('');
      const res = await orderService.create({
        customer_id: tmCustomer.id,
        order_type: 'STANDARD',
        order_style: newOrderStyle,
        description: tmDescription?.trim() || null,
        po_number: tmPoNumber?.trim() || null,
        order_date: formatLocalTodayYmd(),
        tax_mode: 'none',
        tax_basis: 'document',
        lineItems: []
      });
      const created = res?.data;
      setNewTmDialogOpen(false);
      setTmCustomer(null);
      setTmDescription('');
      setTmPoNumber('');
      setNewOrderStyle('standard');
      if (created?.id) {
        navigate(`/work-orders/${created.id}`);
      }
      await loadOrders();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setTmSaving(false);
    }
  };

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (colDateFrom) {
      const from = parseLocalDate(colDateFrom);
      if (from) list = list.filter((o) => {
        const od = o.order_date ? parseLocalDate(o.order_date) : null;
        return od ? od.getTime() >= from.getTime() : false;
      });
    }
    if (colDateTo) {
      const to = parseLocalDate(colDateTo);
      if (to) list = list.filter((o) => {
        const od = o.order_date ? parseLocalDate(o.order_date) : null;
        return od ? od.getTime() <= to.getTime() : false;
      });
    }
    const getSortValue = (o) => {
      switch (sortKey) {
        case 'order_identifier':
          return String(o.identifier ?? o.work_order_number ?? o.work_order_id ?? '');
        case 'quote_identifier':
          return String(o.quote_identifier ?? o.quote_number ?? o.quote_id ?? '');
        case 'order_date':
          return o.order_date
            ? (parseLocalDate(o.order_date)?.getTime() ?? 0)
            : (o.created_at ? new Date(o.created_at).getTime() : 0);
        case 'due_date':
          return o.due_date
            ? (parseLocalDate(o.due_date)?.getTime() ?? 0)
            : 0;
        case 'customer_name':
          return String(o.company_name || [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') || '').toLowerCase();
        case 'customer_po':
          return String(o.po_number ?? '');
        case 'total_amount':
          return o.total_amount != null ? Number(o.total_amount) : 0;
        case 'uninvoiced_amount':
          return o.uninvoiced_amount != null ? Number(o.uninvoiced_amount) : 0;
        case 'open_ar':
          return o.open_invoice_balance_due != null ? Number(o.open_invoice_balance_due) : 0;
        case 'salesman':
          return String(`${o.salesman_first_name || ''} ${o.salesman_last_name || ''}`.trim() || o.salesman_name || '').toLowerCase();
        case 'status':
          return String(o.status ?? '').toLowerCase();
        case 'notes':
          return String(o.description || o.notes || '').toLowerCase();
        default:
          return '';
      }
    };

    const dir = sortDirection === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (sortKey === 'order_date') {
        const va = getSortValue(a);
        const vb = getSortValue(b);
        if (va !== vb) return dir * (va - vb);
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dir * (ta - tb);
      }
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    });

    return list;
  }, [orders, colDateFrom, colDateTo, sortKey, sortDirection]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [tableSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(ORDER_LIST_HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  const ordersFilterKeyRef = useRef(null);

  const loadOrders = useCallback(async () => {
    const filterKey = [
      statusFilter,
      customerFilter || '',
      partFilter || '',
      String(showCancelledOrders),
      String(showSupersededOrders),
      debouncedTableSearch,
      JSON.stringify(debouncedHeaderColFilters)
    ].join('|');
    const filterJustChanged =
      ordersFilterKeyRef.current != null && ordersFilterKeyRef.current !== filterKey;
    ordersFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        search: debouncedTableSearch,
        status: statusFilter || undefined,
        ...(customerFilter ? { customerId: customerFilter } : {}),
        ...(partFilter ? { part_id: partFilter } : {}),
        ...(showCancelledOrders ? { include_cancelled: 'true' } : {}),
        ...(showSupersededOrders ? { include_superseded: 'true' } : {}),
        ...workOrderColumnFilterQueryParams(debouncedHeaderColFilters)
      };
      const response = await orderService.getAll(params);
      const list = Array.isArray(response?.data) ? response.data : [];
      const total = typeof response?.total === 'number' ? response.total : list.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setOrdersTotal(total);
      setOrders(list);
      try {
        const slipsRes = await packingSlipService.getAll({ limit: 2000, offset: 0 });
        const slips = slipsRes?.data || [];
        const map = {};
        slips.forEach((s) => {
          if (s?.work_order_id != null) map[String(s.work_order_id)] = s;
        });
        setPackingSlipByOrderId(map);
      } catch (slipErr) {
        console.warn('Could not preload packing slips for order actions:', slipErr);
        setPackingSlipByOrderId({});
      }
      setSelectedOrderForHeader(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    customerFilter,
    partFilter,
    showCancelledOrders,
    showSupersededOrders,
    debouncedTableSearch,
    debouncedHeaderColFilters,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const oid = selectedOrderForHeader?.id;
    if (!oid) {
      setHeaderAmtPaid(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await orderService.getById(oid);
        const payload = res?.data ?? res;
        const rollup = payload?.invoice_rollup;
        const raw =
          rollup?.amount_paid_total != null
            ? Number(rollup.amount_paid_total)
            : null;
        if (!cancelled) {
          setHeaderAmtPaid(Number.isFinite(raw) ? raw : 0);
        }
      } catch {
        if (!cancelled) setHeaderAmtPaid(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedOrderForHeader?.id]);

  const getExistingPackingSlip = async (orderId) => {
    if (!orderId) return null;
    const existingRes = await packingSlipService.getAll({
      work_order_id: orderId,
      limit: 1,
      offset: 0
    });
    const existing = existingRes?.data?.[0] ?? existingRes?.data?.rows?.[0] ?? null;
    return existing;
  };

  const handleGeneratePackingSlip = async (order) => {
    try {
      setPackingSlipBusyOrderId(order?.id ?? null);
      const existing = await getExistingPackingSlip(order?.id);
      if (existing?.id) {
        navigate(`/packing-slips/${existing.id}`);
      } else {
        // Create flow: user sets ship date / tracking on the creation page.
        navigate(`/packing-slips/new?work_order_id=${order.id}`);
      }
    } catch (err) {
      console.error('Generate packing slip error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to generate packing slip');
    } finally {
      setPackingSlipBusyOrderId(null);
    }
  };

  const handleOpenOrderRevisionDialog = async (order) => {
    setRevisionDialogTitle(`Revision History: ${order.identifier || order.work_order_number}`);
    setRevisionDialogType('order');
    setRevisionDialogItems([]);
    setRevisionDialogLoading(true);
    setRevisionDialogOpen(true);
    try {
      const res = await orderService.getRevisions(order.id);
      setRevisionDialogItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load order revision history:', err);
      setRevisionDialogItems([]);
    } finally {
      setRevisionDialogLoading(false);
    }
  };

  const handleOpenQuoteRevisionDialog = async (order) => {
    if (!order.quote_id) return;
    const quoteLabel = order.quote_identifier || order.quote_number || order.quote_id;
    setRevisionDialogTitle(`Revision History: ${quoteLabel}`);
    setRevisionDialogType('quote');
    setRevisionDialogItems([]);
    setRevisionDialogLoading(true);
    setRevisionDialogOpen(true);
    try {
      const res = await quoteService.getRevisions(order.quote_id);
      setRevisionDialogItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load quote revision history:', err);
      setRevisionDialogItems([]);
    } finally {
      setRevisionDialogLoading(false);
    }
  };

  const handlePrintOrder = (order) => {
    if (!order?.id) return;
    navigate(`/work-orders/${order.id}?print=1`);
  };

  const handlePrintPackingSlip = async (order) => {
    try {
      setPackingSlipBusyOrderId(order?.id ?? null);
      const existing = await getExistingPackingSlip(order?.id);
      if (existing?.id) {
        navigate(`/packing-slips/${existing.id}?print=1`);
      } else {
        // Packing slips are immutable after creation; send user to create page first.
        navigate(`/packing-slips/new?work_order_id=${order.id}`);
      }
    } catch (err) {
      console.error('Print packing slip error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to print packing slip');
    } finally {
      setPackingSlipBusyOrderId(null);
    }
  };

  const handleOrderClick = (orderId) => {
    navigate(`/work-orders/${orderId}`);
  };

  const handleOrderRowClick = (order) => {
    setSelectedOrderForHeader(order);
  };

  const handleMenuOpen = (event, order) => {
    event.stopPropagation();
    setSelectedOrder(order);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteFromMenu = () => {
    if (!selectedOrder) return;
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrder?.id) return;
    try {
      setDeleting(true);
      setError('');
      await orderService.delete(selectedOrder.id);
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      await loadOrders();
    } catch (err) {
      console.error('Error deleting work order:', err);
      setError(err.response?.data?.message || 'Failed to delete work order');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'info',
      waiting_external: 'warning',
      in_progress: 'warning',
      Partial: 'warning',
      'Ready to Ship': 'warning',
      shipped: 'primary',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const isShippedOrder = (order) => String(order?.status || '').trim().toLowerCase() === 'shipped';
  /** Shipped order with billing left: 'Uninvoiced' (no invoices) or 'Partially invoiced'. */
  const uninvoicedChipLabel = (order) => {
    if (!isShippedOrder(order) || Number(order?.uninvoiced_amount) <= 0.02) return null;
    return Number(order?.invoiced_total_for_order) > 0.02 ? 'Partially invoiced' : 'Uninvoiced';
  };

  const getStatusChipSx = (status) => {
    if (status === 'open') {
      return { textTransform: 'uppercase', backgroundColor: '#FFE0B2', color: '#E65100', fontWeight: 700 };
    }
    if (status === 'waiting_external') {
      return { textTransform: 'uppercase', backgroundColor: '#FFF3E0', color: '#EF6C00', fontWeight: 700 };
    }
    if (status === 'Partial') {
      return { textTransform: 'uppercase', backgroundColor: '#FFF9C4', color: '#F57F17', fontWeight: 700 };
    }
    if (status === 'Ready to Ship') {
      return { textTransform: 'uppercase', backgroundColor: '#C8E6C9', color: '#1B5E20', fontWeight: 700 };
    }
    if (status === 'shipped') {
      return { textTransform: 'uppercase', backgroundColor: '#BBDEFB', color: '#0D47A1', fontWeight: 700 };
    }
    return { textTransform: 'capitalize' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatDateOnly(dateString, 'MM/dd/yyyy') || '';
  };

  const formatOrderDate = (order) => {
    if (order?.order_date) return formatDateOnly(order.order_date, 'EEE M/d') || '—';
    if (order?.created_at) return format(new Date(order.created_at), 'EEE M/d');
    return '—';
  };

  const formatOrderDueDate = (order) => {
    if (order?.due_date) return formatDateOnly(order.due_date, 'EEE M/d') || '—';
    return '—';
  };

  const formatOrderTime = (order) => {
    const dt = order.created_at;
    if (!dt) return '—';
    try {
      return format(new Date(dt), 'h:mma');
    } catch {
      return '—';
    }
  };

  const formatOrderDateTime = (order) => {
    const datePart = formatOrderDate(order);
    const timePart = formatOrderTime(order);
    if (datePart === '—' && timePart === '—') return '—';
    if (timePart === '—') return datePart;
    if (datePart === '—') return timePart;
    return `${datePart} ${timePart}`;
  };

  const printVisibleColumns = useMemo(
    () => ORDER_COLUMNS.filter((c) => c.key !== 'actions' && isOrderColVisible(c.key, colVisible)),
    [colVisible]
  );

  const renderOrderPrintCell = (colKey, order) => {
    switch (colKey) {
      case 'order':
        return order.identifier || order.work_order_number;
      case 'quote':
        return order.quote_identifier ?? order.quote_number ?? '—';
      case 'dateTime':
        return formatOrderDateTime(order);
      case 'dueDate':
        return formatOrderDueDate(order);
      case 'customer':
        return (
          order.company_name ||
          [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') ||
          '—'
        );
      case 'customerPo':
        return order.po_number || '—';
      case 'total':
        return formatCurrency(order.total_amount, '—');
      case 'uninvoiced':
        return Number(order.uninvoiced_amount) > 0.02 ? formatCurrency(order.uninvoiced_amount, '—') : '—';
      case 'openAr':
        return Number(order.open_invoice_balance_due) > 0.02
          ? formatCurrency(order.open_invoice_balance_due, '—')
          : '—';
      case 'salesman':
        return order.salesman_first_name && order.salesman_last_name
          ? `${order.salesman_first_name} ${order.salesman_last_name}`
          : '—';
      case 'status': {
        const extras = [];
        if (order.has_past_due_invoice) extras.push('Past due');
        const uninvChip = uninvoicedChipLabel(order);
        if (uninvChip) extras.push(uninvChip);
        return [order.status || '—', ...extras].join(' · ');
      }
      case 'notes':
        return order.description || order.notes || '—';
      default:
        return '—';
    }
  };

  const selectedOrderHasPackingSlip = !!(selectedOrder && packingSlipByOrderId[String(selectedOrder.id)]);
  const selectedOrderHasInvoice = !!selectedOrder?.customer_invoice_id;
  const selectedOrderDeleteBlocked = selectedOrderHasPackingSlip || selectedOrderHasInvoice;
  const selectedOrderDeleteBlockedReason = selectedOrderHasPackingSlip && selectedOrderHasInvoice
    ? 'Cannot delete: order has a packing slip and an invoice.'
    : selectedOrderHasPackingSlip
      ? 'Cannot delete: order has a packing slip.'
      : selectedOrderHasInvoice
        ? 'Cannot delete: order has an invoice.'
        : '';

  /** Tighter than MUI `size="small"` alone — padding, type scale, chips, row actions. */
  const ordersTableDenseSx = {
    '& .MuiTableCell-root': {
      py: 0.35,
      px: '8px',
      fontSize: '0.75rem',
      lineHeight: 1.2,
      verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
      py: 0.45,
      fontSize: '0.7rem',
      verticalAlign: 'top'
    },
    '& .MuiTableCell-head .MuiBox-root': { pr: 1 },
    '& .MuiChip-root': {
      height: 20,
      maxWidth: '100%',
      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', lineHeight: 1.1 }
    },
    '& .MuiIconButton-root': { p: 0.25 },
    '& .MuiButton-sizeSmall': {
      py: 0.125,
      px: 0.75,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      minHeight: 24
    },
    '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
      top: -13,
      zIndex: 20,
      backgroundColor: 'background.paper',
      backgroundImage: 'none',
      backgroundClip: 'padding-box',
      opacity: 1,
      overflow: 'hidden',
      borderTop: '1px solid',
      borderTopColor: 'background.paper',
      boxShadow: (theme) =>
        `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
    }
  };

  return (
    <Box>
      <Box className="no-print-work-orders">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Orders
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/quotes/new')}
          >
            New Quote
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewTmDialogOpen(true);
              setError('');
            }}
          >
            New order
          </Button>
        <Box>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Columns" onClick={() => setColumnsDialogOpen(true)}>
            <ViewColumn fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={() => { printWithFilename('Work-Orders'); setPrintMenuAnchor(null); }}>Print list</MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedOrderForHeader?.id) navigate(`/work-orders/${selectedOrderForHeader.id}`);
                setPrintMenuAnchor(null);
              }}
              disabled={!selectedOrderForHeader}
            >
              Reprint selected
            </MenuItem>
          </Menu>
        </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Order Information Bar — minHeight avoids collapse; height auto so no dead flex gap under description (was: inner flex:1 + fixed Paper height). */}
        <Paper
          sx={{
            p: 1,
            mb: 1.5,
            minHeight: 104,
            height: 'auto',
            backgroundColor: 'surfaceMuted.main',
            border: 1,
            borderColor: 'borderSubtle.main',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            overflow: 'hidden',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', flex: '0 0 auto', overflow: 'hidden' }}>
              <Box
                sx={{
                  ...procurementSummaryFieldsRowSx(true),
                  flex: '1 1 auto',
                  maxHeight: 64,
                  overflowY: 'auto',
                }}
              >
              <Box>
                <Typography variant="caption">Order #</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedOrderForHeader?.identifier || selectedOrderForHeader?.work_order_number || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Date Entered</Typography>
                <Typography variant="body2">
                  {selectedOrderForHeader ? formatDate(selectedOrderForHeader.order_date) || '—' : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Customer PO</Typography>
                <Typography variant="body2">{selectedOrderForHeader?.po_number || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption">Customer</Typography>
                <Typography variant="body2">
                  {selectedOrderForHeader?.customer_id ? (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontSize: 'inherit' }}
                      onClick={() => navigate(`/customers/${selectedOrderForHeader.customer_id}`)}
                    >
                      {selectedOrderForHeader.company_name ||
                        [selectedOrderForHeader.customer_first_name, selectedOrderForHeader.customer_last_name].filter(Boolean).join(' ') ||
                        `#${selectedOrderForHeader.customer_id}`}
                    </Button>
                  ) : selectedOrderForHeader ? (
                    selectedOrderForHeader.company_name ||
                    [selectedOrderForHeader.customer_first_name, selectedOrderForHeader.customer_last_name].filter(Boolean).join(' ') ||
                    '—'
                  ) : (
                    '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Order Total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedOrderForHeader ? formatCurrency(selectedOrderForHeader.total_amount, '—') : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Amt Paid</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {headerAmtPaid != null ? formatCurrency(headerAmtPaid, '—') : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Quoted By</Typography>
                <Typography variant="body2">
                  {selectedOrderForHeader?.salesman_first_name && selectedOrderForHeader?.salesman_last_name
                    ? `${selectedOrderForHeader.salesman_first_name} ${selectedOrderForHeader.salesman_last_name}`
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Quote #</Typography>
                <Typography variant="body2">
                  {selectedOrderForHeader?.quote_identifier || selectedOrderForHeader?.quote_number || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption">Status</Typography>
                <Chip
                  label={selectedOrderForHeader?.status || '—'}
                  color={getStatusColor(selectedOrderForHeader?.status)}
                  size="small"
                  sx={{ ...getStatusChipSx(selectedOrderForHeader?.status), height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                />
              </Box>
              <Box>
                <Typography variant="caption">Due Date</Typography>
                <Typography variant="body2">
                  {selectedOrderForHeader?.due_date ? formatDate(selectedOrderForHeader.due_date) : '—'}
                </Typography>
              </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setSelectedOrderForHeader(null)}
                disabled={!selectedOrderForHeader}
                sx={{ flexShrink: 0 }}
                title="Clear selection"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                minWidth: 0,
                width: '100%',
                mt: 0.375,
                pt: 0.375,
                pb: 0.125,
                borderTop: '1px solid',
                borderColor: 'divider',
                maxHeight: 52,
                boxSizing: 'border-box',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <ProcurementSummaryNotesDense
                label="Description/Notes"
                text={selectedOrderForHeader?.description || selectedOrderForHeader?.notes}
              />
            </Box>
          </Box>
        </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<FilterList />}
            size="small"
          >
            Search Options
          </Button>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {ordersTotal === 0
              ? 'No orders match the current filters.'
              : orders.length === 0
                ? `Page ${page + 1} · ${ordersTotal} order${ordersTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + orders.length} of ${ordersTotal} order${ordersTotal === 1 ? '' : 's'}`}
            {customerFilter ? ' · filtered by customer' : ''}
            {partFilter ? ' · filtered by part' : ''}
            {showCancelledOrders ? ' · including cancelled' : ''}
            {showSupersededOrders ? ' · including prior revisions' : ''}
            {debouncedTableSearch || Object.values(debouncedHeaderColFilters).some(Boolean)
              ? ' · list filtered by search / column filters'
              : ''}
          </Typography>
          <Button variant="outlined" size="small" startIcon={<FilterList />}>
            Recent
          </Button>
          <Button variant="outlined" size="small" startIcon={<Print />}>
            Reprint
          </Button>
        </Box>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280, maxWidth: 500 }}>
            <TextField
              size="small"
              fullWidth
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search all orders — ID, customer, PO, quote #, notes, status, total, date, salesman…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: tableSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setTableSearch('')}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewColumn />}
            onClick={() => setColumnsDialogOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Columns
          </Button>
          {(hasActiveFilters || customerFilter || partFilter) ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Filter by date:
            </Typography>
            <TextField
              size="small"
              type="date"
              placeholder="From"
              value={colDateFrom}
              onChange={(e) => setColDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-input': { minWidth: 130 } }}
              inputProps={{ 'aria-label': 'Date from' }}
            />
            <Typography variant="body2" color="text.secondary">to</Typography>
            <TextField
              size="small"
              type="date"
              placeholder="To"
              value={colDateTo}
              onChange={(e) => setColDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-input': { minWidth: 130 } }}
              inputProps={{ 'aria-label': 'Date to' }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showCancelledOrders}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setShowCancelledOrders(v);
                    try {
                      window.localStorage.setItem('wm_orders_show_cancelled', v ? 'true' : 'false');
                    } catch {
                      /* ignore */
                    }
                  }}
                  size="small"
                />
              )}
              label={<Typography variant="body2">Show cancelled</Typography>}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showSupersededOrders}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setShowSupersededOrders(v);
                    try {
                      window.localStorage.setItem('wm_orders_show_superseded', v ? 'true' : 'false');
                    } catch {
                      /* ignore */
                    }
                  }}
                  size="small"
                />
              )}
              label={<Typography variant="body2">Show prior revisions</Typography>}
            />
          </Box>
        </Box>
      </Paper>

      {/* Orders Table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          overflowX: 'auto',
          scrollbarGutter: 'stable'
        }}
      >
        <Table size="small" stickyHeader sx={ordersTableDenseSx}>
          <TableHead>
            <TableRow>
              {isOrderColVisible('order', colVisible) ? (
              <ResizableHeaderCell width={colWidths.order} minWidth={ORDER_COL_MIN_WIDTHS.order} onResizeStart={(e) => startResize('order', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'order_identifier'} direction={sortDirection} onClick={() => handleSort('order_identifier')}>
                    Order
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.order}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, order: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('quote', colVisible) ? (
              <ResizableHeaderCell width={colWidths.quote} minWidth={ORDER_COL_MIN_WIDTHS.quote} onResizeStart={(e) => startResize('quote', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'quote_identifier'} direction={sortDirection} onClick={() => handleSort('quote_identifier')}>
                    Quote #
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.quote}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, quote: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('dateTime', colVisible) ? (
              <ResizableHeaderCell width={colWidths.dateTime} minWidth={ORDER_COL_MIN_WIDTHS.dateTime} onResizeStart={(e) => startResize('dateTime', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'order_date'} direction={sortDirection} onClick={() => handleSort('order_date')}>
                    Date
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.dateTime}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, dateTime: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('dueDate', colVisible) ? (
              <ResizableHeaderCell width={colWidths.dueDate} minWidth={ORDER_COL_MIN_WIDTHS.dueDate} onResizeStart={(e) => startResize('dueDate', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'due_date'} direction={sortDirection} onClick={() => handleSort('due_date')}>
                    Due Date
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.dueDate}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, dueDate: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('customer', colVisible) ? (
              <ResizableHeaderCell width={colWidths.customer} minWidth={ORDER_COL_MIN_WIDTHS.customer} onResizeStart={(e) => startResize('customer', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'customer_name'} direction={sortDirection} onClick={() => handleSort('customer_name')}>
                    Customer
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.customer}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, customer: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('customerPo', colVisible) ? (
              <ResizableHeaderCell width={colWidths.customerPo} minWidth={ORDER_COL_MIN_WIDTHS.customerPo} onResizeStart={(e) => startResize('customerPo', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'customer_po'} direction={sortDirection} onClick={() => handleSort('customer_po')}>
                    Customer PO
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.po}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, po: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('total', colVisible) ? (
              <ResizableHeaderCell width={colWidths.total} minWidth={ORDER_COL_MIN_WIDTHS.total} onResizeStart={(e) => startResize('total', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'total_amount'} direction={sortDirection} onClick={() => handleSort('total_amount')}>
                    Order Total
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.total}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, total: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('uninvoiced', colVisible) ? (
              <ResizableHeaderCell width={colWidths.uninvoiced} minWidth={ORDER_COL_MIN_WIDTHS.uninvoiced} onResizeStart={(e) => startResize('uninvoiced', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'uninvoiced_amount'} direction={sortDirection} onClick={() => handleSort('uninvoiced_amount')}>
                    Uninvoiced
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.uninvoiced}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, uninvoiced: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('openAr', colVisible) ? (
              <ResizableHeaderCell width={colWidths.openAr} minWidth={ORDER_COL_MIN_WIDTHS.openAr} onResizeStart={(e) => startResize('openAr', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'open_ar'} direction={sortDirection} onClick={() => handleSort('open_ar')}>
                    Open AR
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.openAr}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, openAr: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('salesman', colVisible) ? (
              <ResizableHeaderCell width={colWidths.salesman} minWidth={ORDER_COL_MIN_WIDTHS.salesman} onResizeStart={(e) => startResize('salesman', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'salesman'} direction={sortDirection} onClick={() => handleSort('salesman')}>
                    Salesman
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.salesman}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, salesman: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('status', colVisible) ? (
              <ResizableHeaderCell width={colWidths.status} minWidth={ORDER_COL_MIN_WIDTHS.status} onResizeStart={(e) => startResize('status', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'status'} direction={sortDirection} onClick={() => handleSort('status')}>
                    Status
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.status}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, status: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('notes', colVisible) ? (
              <ResizableHeaderCell width={colWidths.notes} minWidth={ORDER_COL_MIN_WIDTHS.notes} onResizeStart={(e) => startResize('notes', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                  <TableSortLabel active={sortKey === 'notes'} direction={sortDirection} onClick={() => handleSort('notes')}>
                    Notes
                  </TableSortLabel>
                  </Box>
                  <TextField
                    size="small"
                    placeholder="Filter…"
                    value={headerColFilters.notes}
                    onChange={(e) => setHeaderColFilters((p) => ({ ...p, notes: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={orderHeaderFilterTextFieldSx}
                  />
                </Box>
              </ResizableHeaderCell>
              ) : null}
              {isOrderColVisible('actions', colVisible) ? (
              <ResizableHeaderCell width={colWidths.actions} minWidth={ORDER_COL_MIN_WIDTHS.actions} onResizeStart={(e) => startResize('actions', e)}>
                <Box sx={orderColHeaderStackSx}>
                  <Box sx={orderColHeaderLabelSlotSx}>
                    <Typography variant="caption" sx={orderColHeaderCaptionInSlotSx}>
                      Actions
                    </Typography>
                  </Box>
                </Box>
              </ResizableHeaderCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleOrderColumnCount || 1} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleOrderColumnCount || 1} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {orders.length === 0
                      ? 'No orders match the current filters.'
                      : 'No orders on this page match the date range (clear date filters or adjust column filters).'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => {
                const hasPackingSlip = !!packingSlipByOrderId[String(order.id)];
                return (
                <TableRow
                  key={order.id}
                  hover
                  onClick={() => {
                    handleOrderRowClick(order);
                  }}
                  onDoubleClick={() => handleOrderClick(order.id)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedOrderForHeader?.id === order.id ? 'action.selected' : 'inherit',
                    ...((order.status === 'cancelled' || order.superseded_by_work_order_id != null)
                      ? { opacity: 0.9 }
                      : {})
                  }}
                >
                  {isOrderColVisible('order', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.order}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <span>{order.identifier || order.work_order_number}</span>
                      {order.superseded_by_work_order_id != null && (
                        <Tooltip
                          title={`Superseded by ${order.superseded_by_work_order_identifier ?? order.superseded_by_work_order_number ?? `#${order.superseded_by_work_order_id}`}`}
                        >
                          <Chip label="Prior revision" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('quote', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.quote}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {order.quote_id != null ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quotes/${order.quote_id}`);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          {order.quote_identifier ?? order.quote_number ?? order.quote_id}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </Box>
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('dateTime', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.dateTime}>{formatOrderDateTime(order)}</TableCell>
                  ) : null}
                  {isOrderColVisible('dueDate', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.dueDate}>{formatOrderDueDate(order)}</TableCell>
                  ) : null}
                  {isOrderColVisible('customer', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.customer}>
                    {order.company_name || [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') || '-'}
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('customerPo', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.customerPo}>{order.po_number || '-'}</TableCell>
                  ) : null}
                  {isOrderColVisible('total', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.total}>
                    {formatCurrency(order.total_amount, '-')}
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('uninvoiced', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.uninvoiced} align="right">
                    {Number(order.uninvoiced_amount) > 0.02 ? formatCurrency(order.uninvoiced_amount, '-') : '—'}
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('openAr', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.openAr} align="right">
                    {Number(order.open_invoice_balance_due) > 0.02 ? formatCurrency(order.open_invoice_balance_due, '-') : '—'}
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('salesman', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.salesman}>
                    {order.salesman_first_name && order.salesman_last_name
                      ? `${order.salesman_first_name} ${order.salesman_last_name}`
                      : '-'}
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('status', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.status}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      <Chip
                        label={order.status || 'open'}
                        size="small"
                        color={getStatusColor(order.status)}
                        sx={getStatusChipSx(order.status)}
                      />
                      {order.has_past_due_invoice ? (
                        <Chip label="Past due" size="small" color="error" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                      ) : null}
                      {uninvoicedChipLabel(order) ? (
                        <Chip
                          label={uninvoicedChipLabel(order)}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      ) : null}
                    </Box>
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('notes', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.notes}>
                    {order.description || order.notes
                      ? (order.description || order.notes).substring(0, 50) + '...'
                      : '-'}
                  </TableCell>
                  ) : null}
                  {isOrderColVisible('actions', colVisible) ? (
                  <TableCell sx={orderListColumnCellSx.actions} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Print order"
                        onClick={(e) => { e.stopPropagation(); handlePrintOrder(order); }}
                      >
                        <Print fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={hasPackingSlip ? 'success' : 'primary'}
                        title={hasPackingSlip ? 'View packing slip' : 'Generate packing slip'}
                        onClick={(e) => { e.stopPropagation(); handleGeneratePackingSlip(order); }}
                        disabled={packingSlipBusyOrderId === order?.id}
                      >
                        <Receipt fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Print packing slip"
                        onClick={(e) => { e.stopPropagation(); handlePrintPackingSlip(order); }}
                        disabled={packingSlipBusyOrderId === order?.id}
                      >
                        <LocalShipping fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, order)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </TableCell>
                  ) : null}
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100, 200]}
          count={ordersTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            try {
              window.localStorage.setItem('wm_orders_rows_per_page', String(next));
            } catch {
              // ignore quota / private mode
            }
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>

      </Box>

      {/* Print view – work orders list (outside no-print so @media print can show it) */}
      <PrintPage title="Work Orders" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {filteredOrders.length} record(s). Printed {format(new Date(), 'MM/dd/yyyy HH:mm')}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {printVisibleColumns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: 6,
                    textAlign: ['uninvoiced', 'openAr', 'total'].includes(col.key) ? 'right' : 'left'
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((o) => (
              <tr key={o.id}>
                {printVisibleColumns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      padding: 6,
                      textAlign: ['uninvoiced', 'openAr', 'total'].includes(col.key) ? 'right' : 'left'
                    }}
                  >
                    {renderOrderPrintCell(col.key, o)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </PrintPage>

      {/* Column visibility and width settings */}
      <Dialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        aria-labelledby="columns-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="columns-dialog-title">Columns</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Choose which columns appear on the Orders table and adjust their widths.
          </DialogContentText>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Show columns
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.25, mb: 2.5 }}>
            {ORDER_COLUMNS.map((col) => (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    size="small"
                    checked={colVisible[col.key] !== false}
                    onChange={(e) => {
                      const next = e.target.checked;
                      if (!next) {
                        const visibleCount = ORDER_COLUMNS.filter(
                          (c) => colVisible[c.key] !== false
                        ).length;
                        if (visibleCount <= 1) return;
                      }
                      setColVisible((prev) => ({ ...prev, [col.key]: next }));
                    }}
                  />
                }
                label={col.label}
              />
            ))}
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Column widths (px)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {ORDER_COLUMNS.filter((col) => colVisible[col.key] !== false).map((col) => (
              <TextField
                key={col.key}
                label={`${col.label}`}
                type="number"
                size="small"
                value={colWidths[col.key]}
                onChange={(e) =>
                  setColWidths((prev) => ({ ...prev, [col.key]: Number(e.target.value) || 0 }))
                }
                inputProps={{ min: ORDER_COL_MIN_WIDTHS[col.key] || 80, step: 10 }}
              />
            ))}
            {ORDER_COLUMNS.every((col) => colVisible[col.key] === false) ? (
              <Typography variant="body2" color="text.secondary">
                Turn on at least one column to set widths.
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetOrderColumnSettings} color="inherit">
            Reset
          </Button>
          <Button onClick={() => setColumnsDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              saveColWidths(colWidths);
              saveOrderColVisible(colVisible);
              setColumnsDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog
        open={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        aria-labelledby="wo-revision-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="wo-revision-history-dialog-title">{revisionDialogTitle}</DialogTitle>
        <DialogContent>
          {revisionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : revisionDialogItems.length === 0 ? (
            <DialogContentText>No previous revisions found.</DialogContentText>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{revisionDialogType === 'order' ? 'Order #' : 'Quote #'}</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {revisionDialogItems.map((rev) => (
                  <TableRow key={rev.id}>
                    <TableCell>{rev.identifier}</TableCell>
                    <TableCell>
                      {revisionDialogType === 'order'
                        ? (rev.order_date ? formatDate(rev.order_date) : '—')
                        : (rev.quote_date ? formatDate(rev.quote_date) : '—')}
                    </TableCell>
                    <TableCell>
                      <Chip label={rev.status || '—'} size="small" color={getStatusColor(rev.status)} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>{formatCurrency(rev.total_amount, '—')}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setRevisionDialogOpen(false);
                          if (revisionDialogType === 'order') {
                            navigate(`/work-orders/${rev.id}`);
                          } else {
                            navigate(`/quotes/${rev.id}`);
                          }
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedOrder) handleOrderClick(selectedOrder.id);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Copy</MenuItem>
        <MenuItem onClick={handleMenuClose}>Print</MenuItem>
        <MenuItem
          onClick={handleDeleteFromMenu}
          disabled={selectedOrderDeleteBlocked}
          sx={{ color: selectedOrderDeleteBlocked ? 'text.disabled' : 'error.main' }}
          title={selectedOrderDeleteBlockedReason || 'Delete work order'}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={newTmDialogOpen}
        onClose={() => {
          if (!tmSaving) {
            setNewTmDialogOpen(false);
            setNewOrderStyle('standard');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create an order without a quote. For standard orders, add parts and services on the order page. Field Work / Service orders use the same page for
            description and totals workflow similar to that quote style.
          </DialogContentText>
          <FormControl component="fieldset" variant="standard" sx={{ mb: 1, display: 'block' }}>
            <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Order style
            </FormLabel>
            <RadioGroup
              value={newOrderStyle}
              onChange={(e) => setNewOrderStyle(e.target.value)}
            >
              <FormControlLabel value="standard" control={<Radio size="small" />} label="Standard (line items)" />
              <FormControlLabel
                value="scope_of_work"
                control={<Radio size="small" />}
                label="Field Work / Service"
              />
            </RadioGroup>
          </FormControl>
          <Autocomplete
            options={tmCustomers}
            getOptionLabel={(c) =>
              c?.company_name ||
              [c?.first_name, c?.last_name].filter(Boolean).join(' ') ||
              (c?.customer_number != null ? `Customer #${c.customer_number}` : '') ||
              `ID ${c?.id ?? ''}`
            }
            value={tmCustomer}
            onChange={(e, val) => setTmCustomer(val)}
            renderInput={(params) => <TextField {...params} label="Customer" size="small" margin="normal" required />}
          />
          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            value={tmDescription}
            onChange={(e) => setTmDescription(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            size="small"
            label="Customer PO (optional)"
            value={tmPoNumber}
            onChange={(e) => setTmPoNumber(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewTmDialogOpen(false);
              setNewOrderStyle('standard');
            }}
            disabled={tmSaving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateTmOrder} disabled={tmSaving}>
            {tmSaving ? 'Creating…' : 'Create order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Work Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedOrderDeleteBlocked
              ? `Order ${selectedOrder?.identifier || selectedOrder?.work_order_number || ''} cannot be deleted because it has${selectedOrderHasPackingSlip ? ' a packing slip' : ''}${selectedOrderHasPackingSlip && selectedOrderHasInvoice ? ' and' : ''}${selectedOrderHasInvoice ? ' an invoice' : ''}.`
              : `Delete order ${selectedOrder?.identifier || selectedOrder?.work_order_number || ''}? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting || selectedOrderDeleteBlocked}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrders;

